import { Controller } from "@hotwired/stimulus"

const trackedForms = new WeakSet()

// Leva a sugestão da IA para o campo do assistente de publicação. Quando a sugestão chega, ela
// pré-preenche só o campo vazio, ou o que ainda tem o valor que a própria IA pôs, e marca o campo
// com "Sugerido pela IA · revise" — o editor vê o que veio da IA antes de publicar. O resto é por
// clique em "Usar". Campo mexido à mão perde o selo e não é mais pré-preenchido, nem se ficou
// vazio: apagar a sugestão é uma escolha, e um "Regenerar" depois não pode desfazê-la.
//
// Montado também no form do assistente (sem prefill), para registrar as edições à mão feitas
// enquanto a IA ainda está gerando. Os valores chegam como JSON nos params do Stimulus (que faz
// JSON.parse), para um texto como "123" não virar número no caminho.
export default class extends Controller {
  static values = { prefill: Object }

  connect() {
    const form = this.element.closest("form")
    if (form) this.#trackManualEdits(form)

    for (const [id, value] of Object.entries(this.prefillValue)) {
      const field = this.#field(id)
      if (field && this.#untouched(field)) this.#fill(field, value, { prefilled: true })
    }

    // O controller no form só registra edições; cada painel sincroniza seus próprios radios.
    if (!form || this.element === form) return
    this.form = form
    this.syncSelection = ({ target }) => {
      // O input do radio antecede seu change: não desfazer a escolha antes de ela ser aplicada.
      if (target.matches('input[type="radio"]')) return
      this.#syncSelection()
    }
    form.addEventListener("input", this.syncSelection)
    form.addEventListener("change", this.syncSelection)
    this.#syncSelection()
  }

  disconnect() {
    this.form?.removeEventListener("input", this.syncSelection)
    this.form?.removeEventListener("change", this.syncSelection)
  }

  use({ type, params: { field, value, custom } }) {
    // Clicar em Personalizado já marcado também precisa abrir o campo para edição.
    if (type === "click" && !custom) return
    const element = this.#field(field)
    if (!element || element.disabled) return

    if (custom) {
      this.#markManual(element)
      this.#syncSelection()
      // O Choices esconde o select original: o foco vai para o seu input editável.
      const editable = element.multiple ? element.closest(".choices")?.querySelector("input") : element
      editable?.focus()
      return
    }

    delete element.dataset.aiCustom
    element.dataset.aiManual = "true"
    this.#fill(element, value, { prefilled: false })
    this.#syncSelection()
  }

  #field(id) {
    return this.element.closest("form")?.querySelector(`#${CSS.escape(id)}`)
  }

  #read(field) {
    if (field.multiple) return Array.from(field.selectedOptions).map((option) => option.value).filter(Boolean)
    return field.value.trim()
  }

  // O marcador `aiManual` fica no próprio campo, então sobrevive à troca do painel (polling,
  // Regenerar). Os eventos que este controller dispara saem com `aiFilling` no campo e não contam;
  // os do Choices quando o editor tira ou põe uma tag, sim.
  #trackManualEdits(form) {
    if (trackedForms.has(form)) return
    trackedForms.add(form)

    const markManual = ({ target }) => {
      if (target instanceof HTMLElement && target.matches('[name^="post["]') && !target.dataset.aiFilling) {
        this.#markManual(target)
      }
    }
    form.addEventListener("input", markManual)
    form.addEventListener("change", markManual)
  }

  #markManual(field) {
    field.dataset.aiManual = "true"
    field.dataset.aiCustom = "true"
    delete field.dataset.aiPrefilled
    this.#wrapper(field).querySelector(":scope > .admin-ai-badge")?.remove()
  }

  #syncSelection() {
    for (const group of this.element.querySelectorAll("[data-ai-suggestion-group]")) {
      const field = this.#field(group.dataset.aiSuggestionGroup)
      if (!field) continue

      const choices = Array.from(group.querySelectorAll('input[type="radio"]'))
      const selected = !field.dataset.aiCustom && choices.find((choice) =>
        choice.dataset.aiSuggestionCustomParam !== "true" &&
        this.#matches(field, JSON.parse(choice.dataset.aiSuggestionValueParam)))

      for (const choice of choices) {
        const custom = choice.dataset.aiSuggestionCustomParam === "true"
        choice.checked = selected ? choice === selected : custom
        choice.disabled = field.disabled
        const label = choice.closest(".admin-ai-choice")
        label.classList.toggle("is-selected", choice.checked)
        choice.closest(".admin-ai-option").classList.toggle("is-selected", choice.checked)
        label.querySelector(".admin-ai-choice__label").textContent = custom ? "Personalizado" : choice.checked ? "Selecionado" : "Usar"
      }
    }
  }

  #matches(field, suggested) {
    const actual = this.#read(field)
    if (Array.isArray(actual)) {
      const normalize = (values) => values.map((value) => String(value).trim().toLocaleLowerCase("pt-BR"))
      return JSON.stringify(normalize(actual)) === JSON.stringify(normalize(Array.isArray(suggested) ? suggested : [suggested]))
    }
    // datetime-local pode acrescentar segundos ao valor renderizado pelo Rails.
    if (field.type === "datetime-local") return actual.slice(0, 16) === String(suggested).slice(0, 16)
    return actual === String(suggested).trim()
  }

  #untouched(field) {
    if (field.dataset.aiManual) return false

    const value = this.#read(field)
    const blank = Array.isArray(value) ? value.length === 0 : value === ""
    return blank || field.dataset.aiPrefilled === JSON.stringify(value)
  }

  #fill(field, value, { prefilled }) {
    field.dataset.aiFilling = "true"
    try {
      if (field.multiple) {
        this.#fillMultiple(field, Array.isArray(value) ? value.map(String) : [String(value)])
      } else {
        field.value = String(value)
        field.dispatchEvent(new Event("input", { bubbles: true }))
        field.dispatchEvent(new Event("change", { bubbles: true }))
      }
    } finally {
      delete field.dataset.aiFilling
    }

    this.#mark(field, prefilled)
  }

  // Com o Choices montado, pelo evento do select2_controller; ainda carregando (ele é lazy), direto
  // nas <option>, que é de onde o Choices lê ao montar. Ele lê o atributo `selected`
  // (defaultSelected), não a propriedade: marcar só a propriedade deixaria o widget vazio com o
  // valor escondido no form.
  #fillMultiple(select, values) {
    if (select.choices) {
      select.dispatchEvent(new CustomEvent("choices:set-values", { detail: { values } }))
      return
    }

    for (const option of select.options) {
      option.selected = false
      option.defaultSelected = false
    }
    for (const value of values) {
      const key = value.toLocaleLowerCase("pt-BR")
      let option = Array.from(select.options).find((o) => o.value.toLocaleLowerCase("pt-BR") === key)
      if (!option) option = new Option(value, value)
      option.defaultSelected = true
      option.selected = true
      select.appendChild(option)
    }
    select.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #mark(field, prefilled) {
    const wrapper = this.#wrapper(field)
    let badge = wrapper.querySelector(":scope > .admin-ai-badge")

    if (!prefilled) {
      delete field.dataset.aiPrefilled
      badge?.remove()
      return
    }

    field.dataset.aiPrefilled = JSON.stringify(this.#read(field))
    if (!badge) {
      badge = document.createElement("div")
      badge.className = "admin-ai-badge"
      badge.innerHTML = '<i class="bi bi-stars"></i> Sugerido pela IA · revise'
      wrapper.appendChild(badge)
    }
  }

  #wrapper(field) {
    return field.closest(".mb-3") || field.parentElement
  }
}
